// Import an existing satsang programme PDF (KrutiDev legacy font, like the official monthly PDF)
// into an editable schedule. Names/ghars are matched by converting our Hindi lists to KrutiDev.
import { GHARS, PATHIS, PREACHERS } from "../data/rssbData";
import { unicodeToKrutidev, krutidevToUnicode } from "./krutidev";
import { buildDates } from "./scheduler";

const squash = (s) => String(s || "").replace(/[\s\-–&,.;:()[\]{}]/g, "");
const kd = (hi) => squash(unicodeToKrutidev(hi || ""));
const DATE_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

async function pageItems(file) {
  const pdfjs = await import("pdfjs-dist/build/pdf");
  const worker = await import("pdfjs-dist/build/pdf.worker.entry");
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default || worker;
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const pages = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const { items } = await page.getTextContent();
    // top-down y (pdf y grows upwards)
    pages.push(items.filter((it) => it.str.trim()).map((it) => ({ s: it.str.trim(), x: it.transform[4], w: it.width || 0, cx: it.transform[4] + (it.width || 0) / 2, y: -it.transform[5] })));
  }
  return pages;
}

export const looseHi = (s) =>
  String(s || "")
    .replace(/^(श्रीमती|श्री|कुमारी)\s*/, "")
    .replace(/[\sािीुूंँ़्–\-.]/g, "")
    .replace(/[नण](?=[क-ह])/g, "")
    .replace(/ष/g, "श")
    .replace(/व/g, "ब");
// Similarity 0..1 (character-pair overlap).
export const dice = (a, b) => {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const pairs = (s) => Array.from({ length: s.length - 1 }, (_, i) => s.slice(i, i + 2));
  const A = pairs(a), B = pairs(b);
  if (!A.length || !B.length) return 0;
  const m = new Map();
  A.forEach((x) => m.set(x, (m.get(x) || 0) + 1));
  let n = 0;
  B.forEach((x) => {
    const c = m.get(x);
    if (c) { n++; m.set(x, c - 1); }
  });
  return (2 * n) / (A.length + B.length);
};
// PDF ghar label (KrutiDev, e.g. "<djok&l-l-") -> similarity with our ghar.
const gharScore = (kdLabel, g) => {
  const name = looseHi(krutidevToUnicode(kdLabel).split("–")[0]);
  return Math.max(dice(name, looseHi(g.hi)), squash(kdLabel).startsWith(kd(g.hi)) ? 1 : 0);
};
// Assign each PDF row label to its best distinct ghar.
export function matchGhars(labels) {
  const pairs = [];
  labels.forEach((l, i) => GHARS.forEach((g) => pairs.push({ i, g, s: gharScore(l, g) })));
  pairs.sort((a, b) => b.s - a.s);
  const out = {}, used = new Set();
  pairs.forEach(({ i, g, s }) => {
    if (s >= 0.4 && !(i in out) && !used.has(g.id)) { out[i] = g; used.add(g.id); }
  });
  return out;
}
// Decoded "श्री नाम–स्थान 9xxxxxxxxx" -> { name, city, mobile }
export function decodeCell(text) {
  const flat = text.replace(/\s+/g, " ");
  const mobile = (flat.replace(/\s/g, "").match(/\d{10}/) || [""])[0];
  const hi = krutidevToUnicode(flat.replace(/[\d\s]+$/, "").replace(/\d{4,}/g, "")).replace(/[(),]/g, " ").replace(/\s+/g, " ").trim();
  const [name, ...rest] = hi.replace(/(रविवार|बुधवार|गुरुवार)\s*/g, "").split("–");
  // re-join words broken by line wraps (short fragments belong to the previous word)
  const glue = (s, max) =>
    s.split(/\s+/).filter((w) => w && !/^\d+$/.test(w)).reduce((acc, w) => {
      if (acc.length && (w.length <= max || /^[\u093E-\u094D\u0902\u0901]/.test(w))) acc[acc.length - 1] += w;
      else acc.push(w);
      return acc;
    }, []).join(" ");
  return { name: glue(name, 1), city: glue(rest.join(" "), 2), mobile };
}

// Best person whose mobile, KrutiDev name or decoded name matches the text.
// true when two numbers differ by at most one typed digit (wrong, missing or extra)
const nearNum = (a, b) => {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0, j = 0, edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { i++; j++; continue; }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else { i++; j++; }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
};

function matchPerson(text, list) {
  const digits = text.replace(/\s/g, "").match(/\d{10}/g) || [];
  const byMob = list.find((p) => p.mobile && digits.some((d) => String(p.mobile).includes(d)));
  if (byMob) return byMob;
  const loose = text.replace(/\s/g, "").match(/\d{9,11}/g) || [];
  const near = list.filter((p) => p.mobile && loose.some((d) => nearNum(d, String(p.mobile))));
  if (near.length === 1) return near[0];
  // mistyped/short number: most digits still in the same order (e.g. 840815529 ~ 8400815594)
  const lcs = (a, b) => {
    const dp = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
    for (let i = 1; i <= a.length; i++)
      for (let j = 1; j <= b.length; j++) dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    return dp[a.length][b.length];
  };
  const close = list.filter((p) => p.mobile && loose.some((d) => lcs(d, String(p.mobile)) >= 8));
  if (close.length === 1) return close[0];
  const t = squash(text);
  let best = null;
  list.forEach((p) => {
    const k = kd(p.hi);
    if (k.length >= 3 && t.includes(k) && (!best || k.length > kd(best.hi).length)) best = p;
  });
  if (best) return best;
  const dc = decodeCell(text);
  const dn = looseHi(dc.name);
  if (dn.length < 3) return null;
  const city = looseHi(dc.city);
  let top = null, topS = 0;
  list.forEach((p) => {
    const s = dice(dn, looseHi(p.hi)) + (city && p.placeHi ? 0.15 * dice(city, looseHi(p.placeHi)) : 0);
    if (s > topS) { top = p; topS = s; }
  });
  return topS >= 0.8 ? top : null;
}

export async function parseSchedulePdf(file, specials = []) {
  const pages = await pageItems(file);
  const cells = {};
  GHARS.forEach((g) => (cells[g.id] = {}));
  const allDates = new Set();
  const gharKeys = GHARS.map((g) => ({ g, k: kd(g.hi) })).sort((a, b) => b.k.length - a.k.length);
  const pathiLabel = kd("पाठी");
  const skLabel = kd("स.क.");
  let lastRow = null;
  // keep the number printed in the PDF; null when it is the same as the saved one
  const pdfMobile = (d, p) => {
    const m = d?.mobile && /^\d{10}$/.test(d.mobile) ? d.mobile : null;
    return p && (!m || m === String(p.mobile)) ? null : m || null;
  };
  const pathiFields = (t) => {
    const pa = t ? matchPerson(t, PATHIS) : null;
    const d = t ? decodeCell(t) : null;
    return { pathiId: pa?.id || null, customPathi: pa ? null : d?.name || null, pathiCity: pa ? null : d?.city || null, pathiMobile: pdfMobile(d, pa) };
  };

  pages.forEach((items) => {
    const dateCols = items.filter((it) => DATE_RE.test(it.s)).map((it) => {
      const [, d, m, y] = it.s.match(DATE_RE);
      return { iso: `${y}-${m}-${d}`, x: it.cx, y: it.y };
    });
    if (!dateCols.length) return;
    dateCols.sort((a, b) => a.x - b.x);
    dateCols.forEach((c) => allDates.add(c.iso));
    const headY = Math.max(...dateCols.map((c) => c.y));
    const half = dateCols.length > 1 ? (dateCols[1].x - dateCols[0].x) / 2 : 25;
    const leftEdge = dateCols[0].x - half;
    const bounds = dateCols.map((c, i) => ({
      ...c,
      from: i ? (dateCols[i - 1].x + c.x) / 2 : leftEdge,
      to: i < dateCols.length - 1 ? (c.x + dateCols[i + 1].x) / 2 : Infinity,
    }));
    const dayWords = new Set(["रविवार", "बुधवार", "गुरुवार", "सोमवार", "मंगलवार", "शुक्रवार", "शनिवार"]);
    const noteY = Math.min(...items.filter((it) => it.y > headY && krutidevToUnicode(it.s).includes("नोट")).map((it) => it.y), Infinity);
    const body = items.filter(
      (it) => it.y > headY + 4 && it.y < noteY - 2 && !dayWords.has(krutidevToUnicode(it.s).trim()) && !/^X+$/i.test(it.s) && !/^(page|of)$/i.test(it.s) && !/page\s*\d+/i.test(it.s)
    );

    // ghar rows: serial number in the first column is the anchor; the ghar is the name printed beside it
    const rows = [];
    const serials = body.filter((it) => it.x < leftEdge && /^\d{1,2}$/.test(it.s) && it.x < dateCols[0].x * 0.35);
    const labelTexts = serials.map((it) =>
      body
        .filter((b) => b.x < leftEdge && b.x > it.x + 2 && Math.abs(b.y - it.y) < 10)
        .sort((a, b) => a.x - b.x)
        .map((b) => b.s)
        .join(" ")
    );
    const matched = matchGhars(labelTexts);
    serials.forEach((it, i) => {
      const g = matched[i];
      if (g && !rows.some((r) => r.g.id === g.id)) rows.push({ g, y: it.y });
    });
    const lines = {};
    body.filter((it) => it.x < leftEdge).forEach((it) => {
      const y = Math.round(it.y / 4) * 4;
      (lines[y] = lines[y] || []).push(it);
    });
    if (!rows.length)
      Object.entries(lines).forEach(([y, its]) => {
        const t = squash(its.sort((a, b) => a.x - b.x).map((i) => i.s).join(""));
        const hit = gharKeys.find(({ k }) => k.length >= 3 && t.includes(k));
        if (hit && !rows.some((r) => r.g.id === hit.g.id)) rows.push({ g: hit.g, y: Number(y) });
      });
    rows.sort((a, b) => a.y - b.y);
    // all row labels (स.क. / पाठी) in order; each label is the vertical centre of its row
    const marks = body
      .filter((it) => it.x < leftEdge && (squash(it.s) === pathiLabel || squash(it.s) === skLabel))
      .map((it) => ({ y: it.y, pa: squash(it.s) === pathiLabel }))
      .sort((a, b) => a.y - b.y);
    const before = (y) => [...marks].reverse().find((m) => m.y < y - 2);
    const after = (y) => marks.find((m) => m.y > y + 2);
    const band = (y, prev, next) => [
      prev ? (prev.y + y) / 2 : headY + 4,
      next ? (y + next.y) / 2 : y + Math.max(16, (y - (prev?.y ?? y - 32)) / 2),
    ];
    const textIn = (c, [t, b]) =>
      body.filter((it) => it.cx >= c.from && it.cx < c.to && it.y >= t && it.y < b).sort((p, q) => p.y - q.y || p.x - q.x).map((i) => i.s).join(" ");

    // पाठी row continued from the previous page (ghar split across pages)
    if (marks[0]?.pa && lastRow) {
      const rng = band(marks[0].y, null, marks[1]);
      bounds.forEach((c) => {
        const cell = cells[lastRow][c.iso];
        const t = textIn(c, rng);
        if (cell && !cell.pathiId && !cell.customPathi && cell.type !== "CD" && cell.type !== "LCD" && squash(t) && !/^X+$/i.test(squash(t)))
          Object.assign(cell, pathiFields(t));
      });
    }

    rows.forEach((r) => {
      const sk = marks.filter((m) => !m.pa).reduce((best, m) => (!best || Math.abs(m.y - r.y) < Math.abs(best.y - r.y) ? m : best), null) || { y: r.y };
      const nx = after(sk.y);
      const pa = nx?.pa ? nx : null;
      const skRng = band(sk.y, before(sk.y), nx);
      const paRng = pa ? band(pa.y, sk, after(pa.y)) : null;
      bounds.forEach((c) => {
        const skText = textIn(c, skRng);
        const paText = paRng ? textIn(c, paRng) : "";
        const all = squash(skText + paText);
        if (!all || /^X+$/i.test(all)) return;
        if (all.includes(kd("सवाल"))) return void (cells[r.g.id][c.iso] = { type: "LCD", preacherId: null, pathiId: null });
        if (/lh0?Mh0?/.test(all) && !matchPerson(skText, PREACHERS))
          return void (cells[r.g.id][c.iso] = { type: "CD", preacherId: null, pathiId: null });
        const pre = matchPerson(skText, PREACHERS);
        const dSk = squash(skText) && !/^X+$/i.test(squash(skText)) ? decodeCell(skText) : null;
        const sp = !pre && specials.find((s) => s.dateIso === c.iso && (s.morningGharId === r.g.id || s.noonGharId === r.g.id));
        const paF = /^X*$/i.test(squash(paText)) ? pathiFields("") : pathiFields(paText);
        cells[r.g.id][c.iso] = sp
          ? { type: "SPECIAL", preacherId: null, ...paF, special: { name: sp.skName, fromCity: sp.fromCity || "", mobile: sp.skMobile || "", slot: sp.noonGharId === r.g.id ? "noon" : "morning", entryId: sp.id } }
          : { type: pre?.role || "SR", preacherId: pre?.id || null, customPreacher: pre ? null : dSk?.name || null, preacherCity: pre ? null : dSk?.city || null, preacherMobile: pdfMobile(dSk, pre), ...paF };
      });
    });
    if (rows.length) lastRow = rows[rows.length - 1].g.id;
  });
  if (!allDates.size) throw new Error("PDF में तारीखें नहीं मिलीं");
  // a city broken by a line wrap ("मुरा दाबाद") takes the spelling seen elsewhere ("मुरादाबाद")
  const known = {};
  const places = [...PATHIS, ...PREACHERS].map((p) => p.placeHi).concat(GHARS.map((g) => g.hi));
  const allCells = Object.values(cells).flatMap((ds) => Object.values(ds));
  allCells.forEach((c) => places.push(c.preacherCity, c.pathiCity));
  places.filter(Boolean).forEach((s) => {
    const k = s.replace(/\s/g, "");
    if (!known[k] || s.split(" ").length < known[k].split(" ").length) known[k] = s;
  });
  allCells.forEach((c) => {
    ["preacherCity", "pathiCity"].forEach((f) => c[f] && (c[f] = known[c[f].replace(/\s/g, "")] || c[f]));
  });
  const [y, m] = [...allDates].sort()[0].split("-").map(Number);
  return { year: y, month: m - 1, dates: buildDates(y, m - 1), cells };
}
